import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import clsx from 'clsx';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

import {
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Checkbox,
  Table,
  TableHead,
  TableBody,
  TableRow,
  Tooltip,
  FormControl,
  FormControlLabel,
} from '@material-ui/core';
import MuiTableCell from '@material-ui/core/TableCell';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';

import AddButton from './AddButton';
import Content from '../Content';
import Autocomplete2 from '../../components/Autocomplete2';

import { gridStyle } from 'utils/formStyles';

import { getService } from '../../reducers/service';
import { findSearchGroups } from 'Settings/Security/SecFunctions';
import { notify } from 'reducers/NotifierReducer';

const useDialogStyles = makeStyles(() => ({
  workflowDialogBox: {
    maxWidth: '100%',
    minHeight: '150px',
  },
  dialogFieldsetWrapperFilter: {
    border: '1px solid #1e8dfa',
    borderRadius: '10px',
    padding: '15px 13px 5px',
    height: '90%',
    width: '100%',
  },
  fieldsetTitleFilter: {
    position: 'absolute',
    marginTop: '-25px',
    color: 'white',
    background: '#1e8dfa',
    borderRadius: '10px',
    padding: '2px 10px',
  },
  item: {
    ...gridStyle(200, 800),
    margin: 0,
  },
}));

const TableCell = withStyles({
  root: {
    borderBottom: 'none',
    padding: '4px 8px',
  },
})(MuiTableCell);

const UI_ONLY_FIELDS = ['id', 'roleName', 'StepRoles', 'StepRole', 'Step', 'Role'];

const toPayload = obj => {
  const payload = { ...obj };
  UI_ONLY_FIELDS.forEach(key => delete payload[key]);
  return payload;
};

let tempIdCounter = 0;
const newTempId = () => `new-${Date.now()}-${tempIdCounter++}`;

const isSameRole = (a, b) =>
  a?.ptsWFlowRoleID ? a.ptsWFlowRoleID === b?.ptsWFlowRoleID : !!a?.id && a.id === b?.id;

const toStepRole = (role, ptsWFlowStepID) => ({
  ptsWFlowStepID,
  RoleID: role.RoleID,
  roleName: role.roleName,
  Advance: Boolean(role.Advance),
  Return: Boolean(role.Return),
  Remove: Boolean(role.Remove),
});

function AddEditDialog(props) {
  const {
    workflowDialog,
    closeWorkflowDialog,
    workFlowDialogType,
    selectedWorkflow,
    allWorkflows,
    fetchedSteps,
    allRoles,
    agencies,
  } = props;

  const dispatch = useDispatch();
  const [roles, setRoles] = useState([]);
  const classes = useDialogStyles();
  const workflowService = getService('ptswflows');
  const workflowStepService = getService('ptswflowsteps');
  const workflowRoleService = getService('ptswflowroles');
  const workflowStepRolesService = getService('ptswflowsteproles');
  const [workflow, setWorkflow] = useState({});
  const [steps, setSteps] = useState([]);
  const [permissionsDialog, setPermissionsDialog] = useState(false);
  const [stepsDialog, setStepsDialog] = useState(false);
  const [workflowStep, setWorkflowStep] = useState({});
  const [currentRole, setCurrentRole] = useState({});
  const [workflowRoles, setWorkflowRoles] = useState([]);
  const [workflowRolesFilter, setWorkflowRolesFilter] = useState('');
  const [filteredWorkflowRoles, setFilteredWorkflowRoles] = useState([]);
  const [stepRolesFilter, setStepRolesFilter] = useState('');
  const [pendingDeletes, setPendingDeletes] = useState({ roleIds: [], stepIds: [], stepRoleIds: [] });
  const [saving, setSaving] = useState(false);
  const initializedRef = useRef(false);
  const lawAgencies = agencies?.filter(item => (item.AgencyType & 1) === 1) || [];

  const getRoleName = role =>
    role?.roleName ||
    allRoles?.find(r => r.RoleID === role?.RoleID)?.roleName ||
    roles.find(r => r.RoleID === role?.RoleID)?.roleName ||
    '';

  const availableRoles = roles.filter(
    role =>
      role.RoleID === currentRole?.RoleID ||
      !workflowRoles.some(wr => wr.RoleID === role.RoleID && !isSameRole(wr, currentRole))
  );

  useEffect(() => {
    if (workflowRolesFilter) {
      const filter = workflowRolesFilter.toLocaleLowerCase();
      const newList = workflowRoles.filter(
        item => getRoleName(item).toLocaleLowerCase().indexOf(filter) !== -1
      );
      setFilteredWorkflowRoles(newList);
    } else {
      setFilteredWorkflowRoles(workflowRoles);
    }
  }, [workflowRolesFilter, workflowRoles, allRoles, roles]);

  useEffect(() => {
    const agencyId = workflow?.AgencyID ? workflow?.AgencyID : '';
    findSearchGroups(agencyId).then(res => {
      let roleData = res.map((role, index) => {
        return { roleName: role.Label, RoleID: role.ptsGroupID };
      });
      setRoles(roleData);
    });
  }, [workflow?.AgencyID]);

  useEffect(() => {
    if (initializedRef.current) return;

    if (workFlowDialogType === 'Add') {
      setSteps([]);
      setWorkflow({});
      setWorkflowRoles([]);
      initializedRef.current = true;
      return;
    }

    const workflowId = selectedWorkflow?.ptsWFlowID;
    const foundWorkflow = allWorkflows?.find(w => w.ptsWFlowID === workflowId);
    if (workFlowDialogType !== 'Edit' || !foundWorkflow || !Array.isArray(fetchedSteps?.data)) {
      return;
    }

    setWorkflow(foundWorkflow);

    const loadedSteps = (foundWorkflow.Step || []).map(step => {
      const foundFetchedStep = fetchedSteps.data.find(
        fetched => fetched?.ptsWFlowStepID === step?.ptsWFlowStepID
      );
      return {
        ...step,
        id: newTempId(),
        StepRoles: (foundFetchedStep?.StepRole || []).map(stepRole => ({ ...stepRole })),
      };
    });
    loadedSteps.sort((a, b) => (a.Order > b.Order ? 1 : -1));
    setSteps(loadedSteps);

    setWorkflowRoles((foundWorkflow.Role || []).map(role => ({ ...role })));
    initializedRef.current = true;
  }, [selectedWorkflow?.ptsWFlowID, allWorkflows, workFlowDialogType, fetchedSteps]);

  const closeStepsDialog = () => {
    setStepsDialog(false);
    setStepRolesFilter('');
  };

  const closePermissionsDialog = () => {
    setPermissionsDialog(false);
  };

  const handleWorkflowChange = (name, value) => {
    setWorkflow({
      ...workflow,
      [name]: value,
    });
  };

  const handleStepChange = (name, value) => {
    setWorkflowStep({
      ...workflowStep,
      ptsWFlowID: selectedWorkflow?.ptsWFlowID,
      [name]: value,
    });
  };

  const handleStepRoleChange = (name, value, roleId) => {
    setWorkflowStep(prevStep => ({
      ...prevStep,
      ptsWFlowID: selectedWorkflow?.ptsWFlowID,
      StepRoles: (prevStep?.StepRoles || []).map(stepRole =>
        stepRole.RoleID === roleId ? { ...stepRole, [name]: value } : stepRole
      ),
    }));
  };

  const handleRoleChange = (name, value) => {
    setCurrentRole({
      ...currentRole,
      ptsWFlowID: selectedWorkflow?.ptsWFlowID,
      [name]: value,
    });
  };

  const handleClickAddPermissions = () => {
    setCurrentRole({ id: newTempId() });
    setPermissionsDialog(true);
  };

  const handleClickAddSteps = () => {
    setWorkflowStep({
      id: newTempId(),
      StepRoles: workflowRoles.map(role => toStepRole(role)),
    });
    setStepsDialog(true);
  };

  const handleWorkflowStepEditClick = step => {
    setWorkflowStep({
      ...step,
      StepRoles: (step.StepRoles || []).map(stepRole => ({ ...stepRole })),
    });
    setStepsDialog(true);
  };

  const handleWorkflowRoleEditClick = role => {
    setCurrentRole({ ...role });
    setPermissionsDialog(true);
  };

  const handleWorkflowRoleDeleteClick = role => {
    const stepRoleIds = [];
    const updatedSteps = steps.map(step => {
      const remainingStepRoles = (step.StepRoles || []).filter(stepRole => {
        if (stepRole.RoleID !== role.RoleID) return true;
        if (stepRole.ptsWFlowStepRoleID) stepRoleIds.push(stepRole.ptsWFlowStepRoleID);
        return false;
      });
      return { ...step, StepRoles: remainingStepRoles };
    });

    setSteps(updatedSteps);
    setWorkflowRoles(workflowRoles.filter(r => !isSameRole(r, role)));
    setPendingDeletes(prev => ({
      ...prev,
      roleIds: role.ptsWFlowRoleID ? [...prev.roleIds, role.ptsWFlowRoleID] : prev.roleIds,
      stepRoleIds: [...prev.stepRoleIds, ...stepRoleIds],
    }));
  };

  const handleWorkflowStepDeleteClick = step => {
    setSteps(steps.filter(s => s.id !== step.id));

    if (step.ptsWFlowStepID) {
      const stepRoleIds = (step.StepRoles || [])
        .filter(stepRole => stepRole.ptsWFlowStepRoleID)
        .map(stepRole => stepRole.ptsWFlowStepRoleID);

      setPendingDeletes(prev => ({
        ...prev,
        stepIds: [...prev.stepIds, step.ptsWFlowStepID],
        stepRoleIds: [...prev.stepRoleIds, ...stepRoleIds],
      }));
    }
  };

  const handleStepSaveClick = () => {
    const savedStep = { ...workflowStep };

    setSteps(prevSteps =>
      prevSteps.some(step => step.id === savedStep.id)
        ? prevSteps.map(step => (step.id === savedStep.id ? savedStep : step))
        : [...prevSteps, savedStep]
    );

    closeStepsDialog();
  };

  const clearOtherDefaultWorkflows = async savedWorkflow => {
    const res = await workflowService.find({
      query: {
        IsDeleted: false,
        AgencyID: savedWorkflow?.AgencyID,
        $limit: 0,
      },
    });

    const otherDefaults = (res?.data || []).filter(
      w => w?.ptsWFlowID !== savedWorkflow?.ptsWFlowID && w?.DefaultForIncident
    );

    await Promise.all(
      otherDefaults.map(w => workflowService.patch(w.ptsWFlowID, { DefaultForIncident: false }))
    );
  };

  const saveRolesAndSteps = async workflowId => {
    await Promise.all(pendingDeletes.stepRoleIds.map(id => workflowStepRolesService.remove(id)));
    await Promise.all([
      ...pendingDeletes.roleIds.map(id => workflowRoleService.remove(id)),
      ...pendingDeletes.stepIds.map(id => workflowStepService.remove(id)),
    ]);

    await Promise.all(
      workflowRoles.map(role => {
        const payload = { ...toPayload(role), ptsWFlowID: workflowId };
        return role.ptsWFlowRoleID
          ? workflowRoleService.patch(role.ptsWFlowRoleID, payload)
          : workflowRoleService.create(payload);
      })
    );

    await Promise.all(
      steps.map(async step => {
        const stepPayload = { ...toPayload(step), ptsWFlowID: workflowId };
        let stepId = step.ptsWFlowStepID;

        if (stepId) {
          await workflowStepService.patch(stepId, stepPayload);
        } else {
          const createdStep = await workflowStepService.create(stepPayload);
          stepId = createdStep?.ptsWFlowStepID;
        }

        await Promise.all(
          (step.StepRoles || []).map(stepRole => {
            const payload = { ...toPayload(stepRole), ptsWFlowStepID: stepId };
            return stepRole.ptsWFlowStepRoleID
              ? workflowStepRolesService.patch(stepRole.ptsWFlowStepRoleID, payload)
              : workflowStepRolesService.create(payload);
          })
        );
      })
    );
  };

  const handleWorkflowSaveClick = async () => {
    if (saving) return;
    setSaving(true);

    try {
      const workflowPayload = toPayload(workflow);
      const savedWorkflow =
        workFlowDialogType === 'Add'
          ? await workflowService.create(workflowPayload)
          : await workflowService.patch(selectedWorkflow.ptsWFlowID, workflowPayload);
      const workflowId = savedWorkflow?.ptsWFlowID || selectedWorkflow?.ptsWFlowID;

      /* Only one default workflow per agency */
      if (savedWorkflow?.DefaultForIncident === true) {
        await clearOtherDefaultWorkflows(savedWorkflow);
      }

      await saveRolesAndSteps(workflowId);

      dispatch(notify('Workflow saved.', 'success'));
      closeWorkflowDialog('reload');
    } catch (err) {
      console.log(err);
      dispatch(notify(`Workflow could not be saved. ${err?.message || ''}`, 'error'));
      setSaving(false);
    }
  };

  const handleSavePermissionsClick = () => {
    if (!currentRole?.RoleID) {
      dispatch(notify('Please select a role.', 'warning'));
      return;
    }

    if (workflowRoles.some(r => r.RoleID === currentRole.RoleID && !isSameRole(r, currentRole))) {
      dispatch(notify('This role is already added to the workflow.', 'warning'));
      return;
    }

    const savedRole = { ...currentRole, ptsWFlowID: selectedWorkflow?.ptsWFlowID };
    const existingRole = workflowRoles.find(r => isSameRole(r, savedRole));
    const previousRoleID = existingRole?.RoleID;
    const roleChanged = !!existingRole && previousRoleID !== savedRole.RoleID;

    setWorkflowRoles(
      existingRole
        ? workflowRoles.map(r => (isSameRole(r, savedRole) ? savedRole : r))
        : [...workflowRoles, savedRole]
    );

    const removedStepRoleIds = [];
    const updatedSteps = steps.map(step => {
      let stepRoles = step.StepRoles || [];
      if (roleChanged) {
        stepRoles = stepRoles.filter(stepRole => {
          if (stepRole.RoleID !== previousRoleID) return true;
          if (stepRole.ptsWFlowStepRoleID) removedStepRoleIds.push(stepRole.ptsWFlowStepRoleID);
          return false;
        });
      }

      if (!stepRoles.some(stepRole => stepRole.RoleID === savedRole.RoleID)) {
        stepRoles = [...stepRoles, toStepRole(savedRole, step.ptsWFlowStepID)];
      }

      return { ...step, StepRoles: stepRoles };
    });

    setSteps(updatedSteps);
    if (removedStepRoleIds.length) {
      setPendingDeletes(prev => ({
        ...prev,
        stepRoleIds: [...prev.stepRoleIds, ...removedStepRoleIds],
      }));
    }
    closePermissionsDialog();
  };

  const handleDragEnd = result => {
    if (!result.destination) return;

    let tempSteps = [...steps];
    let [selectedStep] = tempSteps.splice(result.source.index, 1);
    tempSteps.splice(result.destination.index, 0, selectedStep);

    setSteps(tempSteps.map((step, index) => ({ ...step, Order: index + 1 })));
  };

  const renderWorkflowDialog = () => {
    return (
      <Dialog
        open={workflowDialog}
        onClose={closeWorkflowDialog}
        classes={{
          paperWidthSm: classes.workflowDialogBox,
        }}>
        <DialogTitle
          className="d-flex justify-content-between align-items-center"
          style={{ background: '#1976d2' }}>
          <span style={{ fontSize: '18px', color: 'white' }}>{workFlowDialogType} Workflow</span>
        </DialogTitle>
        <DialogContent>
          <Grid container justify="center" style={{ padding: '4px 20px 0px 5px' }}>
            <Grid container xs={12} spacing={2} alignItems="left">
              <Grid item xs={8}>
                <TextField
                  autoComplete="hidden"
                  size="small"
                  id="standard-textfield-workflow-name"
                  label="Workflow Name"
                  placeholder="Workflow Name"
                  variant="outlined"
                  fullWidth
                  multiline
                  defaultValue={workflow?.Name || ''}
                  onBlur={event => handleWorkflowChange(event.target.name, event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  name="Name"
                />
              </Grid>
              <Grid item xs={3}>
                <Autocomplete2
                  label="Agency"
                  options={lawAgencies}
                  onChange={(e, newValue) =>
                    handleWorkflowChange('AgencyID', newValue?.AgencyID || null)
                  }
                  value={{ AgencyID: workflow?.AgencyID }}
                  getOptionLabel={option => option.AgencyID}
                  getOptionSelected={(option, value) => option.AgencyID === value.AgencyID}
                  renderOption={option => option.AgencyID}
                />
              </Grid>
              <Grid item xs={1}>
                <FormControl>
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="default-incident-checkbox"
                        key={workflow?.DefaultForIncident}
                        checked={workflow?.DefaultForIncident}
                        onChange={event =>
                          handleWorkflowChange(event.target.name, event.target.checked)
                        }
                        name="DefaultForIncident"
                      />
                    }
                    label="Default"
                  />
                </FormControl>
              </Grid>
            </Grid>

            <Grid container xs={12} direction="row" className="mt-2">
              <Grid item xs={5} className="pr-2">
                <div className={clsx(classes.dialogFieldsetWrapperFilter, 'mb-2 mt-2')}>
                  <div className={classes.fieldsetTitleFilter}>Permissions</div>
                  <PerfectScrollbar
                    style={{ maxHeight: '300px' }}
                    options={{ suppressScrollX: true }}>
                    <div className="mt-2">
                      <AddButton
                        tooltipText="Add Permissions"
                        onClick={handleClickAddPermissions}
                      />
                      {/*TODO: Make the Filter dynamic */}
                      <TextField
                        fullWidth
                        label="Filter"
                        variant="outlined"
                        size="small"
                        onChange={e => setWorkflowRolesFilter(e.target.value || '')}
                        style={{ width: '60%', float: 'right' }}
                        value={workflowRolesFilter || ''}
                      />
                    </div>
                    <Table>
                      <TableBody>
                        {filteredWorkflowRoles &&
                          filteredWorkflowRoles.map((role, index) => {
                            return (
                              <TableRow key={index}>
                                <TableCell>{getRoleName(role)}</TableCell>
                                <TableCell className="pr-4">
                                  <Grid container wrap="nowrap" spacing={2} justify="flex-end">
                                    <Tooltip title="Edit Permissions">
                                      <IconButton edge="end" aria-label="edit" className="ml-2">
                                        <EditIcon
                                          onClick={() => handleWorkflowRoleEditClick(role)}
                                        />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete Permissions">
                                      <IconButton
                                        edge="end"
                                        aria-label="delete"
                                        className="ml-1 mr-2">
                                        <DeleteIcon
                                          onClick={() => handleWorkflowRoleDeleteClick(role)}
                                        />
                                      </IconButton>
                                    </Tooltip>
                                  </Grid>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </PerfectScrollbar>
                </div>
              </Grid>
              <Grid item xs={7}>
                <div className={clsx(classes.dialogFieldsetWrapperFilter, 'mb-2 mt-2')}>
                  <div className={classes.fieldsetTitleFilter}>Workflow Steps</div>
                  <PerfectScrollbar
                    style={{ maxHeight: '300px' }}
                    options={{ suppressScrollX: true }}>
                    <div className="mt-2">
                      <AddButton tooltipText="Add Steps" onClick={handleClickAddSteps} />
                    </div>
                    <DragDropContext onDragEnd={handleDragEnd}>
                      <Table>
                        <TableHead>
                          <TableCell>Order</TableCell>
                          <TableCell>Step Name</TableCell>
                          <TableCell>Expiration</TableCell>
                          <TableCell>{''}</TableCell>
                        </TableHead>
                        <Droppable droppableId="steps-table">
                          {provided => (
                            <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                              {steps &&
                                steps?.map((step, index) => (
                                  <Draggable
                                    key={step.id}
                                    draggableId={String(step.id)}
                                    index={index}>
                                    {provided => (
                                      <TableRow
                                        key={step.id}
                                        {...provided.draggableProps}
                                        ref={provided.innerRef}>
                                        <TableCell>{step.Order}</TableCell>
                                        <TableCell {...provided.dragHandleProps}>
                                          {step.Name}
                                        </TableCell>
                                        <TableCell>{step.Expiration}</TableCell>
                                        <TableCell>
                                          <Grid
                                            container
                                            wrap="nowrap"
                                            className="pr-3"
                                            spacing={2}
                                            justify="flex-end">
                                            <Tooltip title="Edit Step">
                                              <IconButton
                                                edge="end"
                                                aria-label="edit"
                                                className="ml-2">
                                                <EditIcon
                                                  onClick={() => handleWorkflowStepEditClick(step)}
                                                />
                                              </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete Step">
                                              <IconButton
                                                edge="end"
                                                aria-label="delete"
                                                className="ml-1 mr-2">
                                                <DeleteIcon
                                                  onClick={() => handleWorkflowStepDeleteClick(step)}
                                                />
                                              </IconButton>
                                            </Tooltip>
                                          </Grid>
                                        </TableCell>
                                      </TableRow>
                                    )}
                                  </Draggable>
                                ))}
                              {provided.placeholder}
                            </TableBody>
                          )}
                        </Droppable>
                      </Table>
                    </DragDropContext>
                  </PerfectScrollbar>
                </div>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Grid
                container
                direction="row"
                justify="center"
                alignItems="center"
                style={{ padding: 5, margin: '-15px 0px 10px' }}>
                <Button
                  style={{ background: '#1976d2', color: '#fff' }}
                  variant="contained"
                  disabled={saving}
                  onClick={handleWorkflowSaveClick}>
                  Save
                </Button>
                <Button
                  onClick={() => closeWorkflowDialog('notReload')}
                  color="primary"
                  style={{ marginRight: 5 }}>
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    );
  };

  const renderStepsDialog = () => {
    return (
      <Dialog
        open={stepsDialog}
        onClose={closeStepsDialog}
        classes={{
          paperWidthSm: classes.dialogBox,
        }}>
        <DialogTitle
          className="d-flex justify-content-between align-items-center"
          style={{ background: '#1976d2' }}>
          <span style={{ fontSize: '18px', color: 'white' }}>
            {workflowStep?.ptsWFlowID ? 'Edit' : 'Add'} Workflow Step
          </span>
        </DialogTitle>
        <DialogContent>
          <Grid container justify="center" style={{ padding: '4px 20px 0px 5px' }}>
            <Grid container xs={12} direction="row">
              <Grid item xs={3} className="mb-2 pr-2" alignItems="center" justifyContent="center">
                <TextField
                  autoComplete="hidden"
                  id="standard-textfield-step-order"
                  fullWidth
                  label="Order"
                  placeholder="Order"
                  variant="outlined"
                  size="small"
                  multiline
                  defaultValue={workflowStep?.Order || ''}
                  onBlur={event => handleStepChange(event.target.name, event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  name="Order"
                />
              </Grid>
              <Grid item xs={6} className="mb-2 pr-2" alignItems="center" justifyContent="center">
                <TextField
                  autoComplete="hidden"
                  id="standard-textfield-step-name"
                  fullWidth
                  label="Step Name"
                  placeholder="Step Name"
                  variant="outlined"
                  size="small"
                  multiline
                  defaultValue={workflowStep?.Name || ''}
                  onBlur={event => handleStepChange(event.target.name, event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  name="Name"
                />
              </Grid>
              <Grid item xs={2} className="mb-2" alignItems="center" justifyContent="center">
                <TextField
                  autoComplete="hidden"
                  id="standard-textfield-step-exp"
                  fullWidth
                  label="Exp"
                  placeholder="Exp"
                  variant="outlined"
                  size="small"
                  defaultValue={workflowStep?.Expiration || ''}
                  onBlur={event => handleStepChange(event.target.name, event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  name="Expiration"
                />
              </Grid>
              <span className="mt-2 ml-2" style={{ fontSize: '15px' }}>
                Days
              </span>
            </Grid>
            <Grid container xs={12}>
              <p style={{ fontSize: '15px' }}>When advancing from this step:</p>
            </Grid>
            <Grid container xs={12} direction="row">
              <Grid item xs={6} className="mb-2 pr-2" alignItems="center" justifyContent="center">
                <FormControl>
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="ibr-alert-checkbox"
                        key={workflowStep?.AlertForIBRErrors}
                        checked={workflowStep?.AlertForIBRErrors}
                        onChange={event =>
                          handleStepChange(event.target.name, event.target.checked)
                        }
                        name="AlertForIBRErrors"
                      />
                    }
                    label="Alert for IBR errors"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6} className="mb-2 " alignItems="center" justifyContent="center">
                <FormControl>
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="promt-approval-checkbox"
                        key={workflowStep?.PromtForApproval}
                        checked={workflowStep?.PromtForApproval}
                        onChange={event =>
                          handleStepChange(event.target.name, event.target.checked)
                        }
                        name="PromtForApproval"
                      />
                    }
                    label="Prompt for Approval"
                  />
                </FormControl>
              </Grid>
            </Grid>

            <div className={clsx(classes.dialogFieldsetWrapperFilter, 'mb-2 mt-2')}>
              <div className={classes.fieldsetTitleFilter}>Permissions</div>
              <Grid container xs={12} direction="row" className="mt-2">
                <TextField
                  fullWidth
                  label="Filter"
                  variant="outlined"
                  size="small"
                  onChange={e => setStepRolesFilter(e.target.value || '')}
                  value={stepRolesFilter}
                />
              </Grid>
              <Grid container xs={12} direction="row" className="mt-2">
                <Table>
                  <TableHead>
                    <TableCell>Role</TableCell>
                    <TableCell>Advance</TableCell>
                    <TableCell>Return</TableCell>
                    <TableCell>Remove</TableCell>
                  </TableHead>
                  <TableBody>
                    {workflowStep?.StepRoles &&
                      workflowStep?.StepRoles?.filter(stepRole =>
                        getRoleName(stepRole)
                          .toLocaleLowerCase()
                          .includes(stepRolesFilter.toLocaleLowerCase())
                      ).map(stepRole => {
                        return (
                          <TableRow key={stepRole?.RoleID}>
                            <TableCell>{getRoleName(stepRole)}</TableCell>
                            <TableCell>
                              <FormControl>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      id="step-advance-checkbox"
                                      key={stepRole?.Advance}
                                      checked={stepRole?.Advance}
                                      onChange={event =>
                                        handleStepRoleChange(
                                          event.target.name,
                                          event.target.checked,
                                          stepRole?.RoleID
                                        )
                                      }
                                      name="Advance"
                                    />
                                  }
                                />
                              </FormControl>
                            </TableCell>
                            <TableCell>
                              <FormControl>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      id="step-return-checkbox"
                                      key={stepRole?.Return}
                                      checked={stepRole?.Return}
                                      onChange={event =>
                                        handleStepRoleChange(
                                          event.target.name,
                                          event.target.checked,
                                          stepRole?.RoleID
                                        )
                                      }
                                      name="Return"
                                    />
                                  }
                                />
                              </FormControl>
                            </TableCell>
                            <TableCell>
                              <FormControl>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      id="step-remove-checkbox"
                                      key={stepRole?.Remove}
                                      checked={stepRole?.Remove}
                                      onChange={event =>
                                        handleStepRoleChange(
                                          event.target.name,
                                          event.target.checked,
                                          stepRole?.RoleID
                                        )
                                      }
                                      name="Remove"
                                    />
                                  }
                                />
                              </FormControl>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </Grid>
            </div>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Grid
                container
                direction="row"
                justify="center"
                alignItems="center"
                style={{ padding: 5, margin: '-15px 0px 10px' }}>
                <Button
                  style={{ background: '#1976d2', color: '#fff' }}
                  variant="contained"
                  onClick={handleStepSaveClick}>
                  Save
                </Button>
                <Button onClick={closeStepsDialog} color="primary" style={{ marginRight: 5 }}>
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    );
  };

  const renderPermissionsDialog = () => {
    return (
      <Dialog
        open={permissionsDialog}
        onClose={closePermissionsDialog}
        classes={{
          paperWidthSm: classes.dialogBox,
        }}>
        <DialogTitle
          className="d-flex justify-content-between align-items-center"
          style={{ background: '#1976d2' }}>
          <span style={{ fontSize: '18px', color: 'white' }}>Default Permissions</span>
        </DialogTitle>
        <DialogContent>
          <Grid container justify="center" style={{ padding: '4px 20px 0px 5px' }}>
            <Grid container xs={12}>
              <Autocomplete2
                label="Role-ADD New"
                className={classes.item}
                options={availableRoles}
                onChange={(e, newValue) => setCurrentRole({ ...currentRole, ...newValue })}
                value={{ ...currentRole, roleName: getRoleName(currentRole) }}
                getOptionLabel={option => option.roleName || ''}
                getOptionSelected={(option, value) => option.roleName === value.roleName}
                renderOption={option => option.roleName}
              />
            </Grid>
            <Grid container xs={12} direction="row" className="ml-2 mt-2">
              <Grid item xs={12}>
                <FormControl>
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="advance-checkbox"
                        key={currentRole?.Advance}
                        checked={currentRole?.Advance}
                        onChange={event =>
                          handleRoleChange(event.target.name, event.target.checked)
                        }
                        name="Advance"
                      />
                    }
                    label="Advance"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} style={{ marginTop: '-20px' }}>
                <FormControl>
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="return-checkbox"
                        key={currentRole?.Return}
                        checked={currentRole?.Return}
                        onChange={event =>
                          handleRoleChange(event.target.name, event.target.checked)
                        }
                        name="Return"
                      />
                    }
                    label="Return"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} style={{ marginTop: '-20px' }}>
                <FormControl>
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="remove-checkbox"
                        key={currentRole?.Remove}
                        checked={currentRole?.Remove}
                        onChange={event =>
                          handleRoleChange(event.target.name, event.target.checked)
                        }
                        name="Remove"
                      />
                    }
                    label="Remove"
                  />
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Grid
                container
                direction="row"
                justify="center"
                alignItems="center"
                style={{ padding: 5, margin: '-15px 0px 10px' }}>
                <Button
                  style={{ background: '#1976d2', color: '#fff' }}
                  variant="contained"
                  onClick={handleSavePermissionsClick}>
                  Save
                </Button>
                <Button onClick={closePermissionsDialog} color="primary" style={{ marginRight: 5 }}>
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Content>
      {renderWorkflowDialog()}
      {renderPermissionsDialog()}
      {renderStepsDialog()}
    </Content>
  );
}

export default AddEditDialog;
